import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Maximize2,
  Undo2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  type Criterios,
  TablaDeCandidatos,
} from "@/components/conciliar/candidatos";
import {
  type Boleta,
  type Candidatos,
  claveDe,
  diaMes,
  dinero,
  type Expediente,
  type Voucher,
} from "@/components/conciliar/comun";
import {
  type Correccion,
  DiceElVoucher,
} from "@/components/conciliar/dice-el-voucher";
import { TiraDelExpediente } from "@/components/conciliar/expediente";
import { RailDeVouchers } from "@/components/conciliar/vouchers";
import { EmptyState } from "@/components/feedback/empty-state";
import { Cabecera } from "@/components/propuesta/cabecera";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

// ===========================================================================
// CONCILIAR EL EXPEDIENTE (#…): atar cada voucher con el abono que lo pagó.
//
// Es la otra dirección del mismo trabajo. La revisión guiada parte del ABONO
// —«¿este abono es el pago de esa boleta?»— y aquí se parte del EXPEDIENTE: un
// PDF trae una o dos boletas y uno o más vouchers, y hasta que cada voucher no
// tenga su movimiento el expediente no está cerrado.
//
// La diferencia no es de gusto. Partiendo del abono, un expediente con tres
// pagos aparece tres veces sin decirlo y nadie sabe si le falta alguno.
// Partiendo del expediente, «faltan dos de tres» se ve de una vez — y la
// boleta y la ficha, que son las que dicen DE QUIÉN es el pago, están delante
// mientras se mira el voucher.
// ===========================================================================

type Busqueda = { archivo?: string; cola: "pendientes" | "todos" };

/** Lo que devuelve una mutación. Se anota a mano por lo mismo que arriba. */
type Respuesta = { status: number; body: { dice?: string; message?: string } };

export const Route = createFileRoute("/_app/conciliar")({
  component: Conciliar,
  validateSearch: (s: Record<string, unknown>): Busqueda => ({
    archivo: typeof s.archivo === "string" ? s.archivo : undefined,
    cola: s.cola === "todos" ? "todos" : "pendientes",
  }),
});

/**
 * La ventana por defecto es la que el motor tiene MEDIDA: del día del pago a
 * siete días después, nunca antes. Una simétrica de tres días suena razonable
 * y no lo es — sobre 603 candidatos con las dos fechas, dejaba pasar 411
 * imposibles, dos de cada tres.
 *
 * La tolerancia de importe sí arranca abierta: cinco soles. El motor exige el
 * céntimo exacto porque cruza solo; aquí mira una persona, y el banco redondea
 * distinto que el voucher lo bastante a menudo como para que buscar exacto
 * esconda justo la línea buena.
 */
/**
 * Las tres formas de no tener nada que hacer, que no son la misma.
 *
 * «Todavía no cargaste» y «ya terminaste» se ven igual desde una lista vacía, y
 * confundirlas manda a buscar un fallo donde solo hay trabajo hecho.
 */
const TITULO_VACIO = {
  sin_periodo: "Todavía no hay ningún periodo abierto",
  sin_expedientes: "Todavía no hay documentos de clientes",
  todo_atado: "No queda ningún expediente por atar",
} as const;

const FRASE_VACIO = {
  sin_periodo:
    "Un periodo es el mes de trabajo. Se abre al cargar el primer archivo, y hasta que exista no hay dónde poner nada.",
  sin_expedientes:
    "Cuando se cargue el primer lote de documentos de clientes, aparecerán aquí para atar cada voucher con el movimiento que lo pagó.",
  todo_atado:
    "Todos los vouchers del periodo tienen su movimiento, o están marcados como que no aparecen en ningún extracto.",
} as const;

const POR_DEFECTO: Criterios = {
  diasAntes: 0,
  diasDespues: 7,
  tolerancia: 500,
  todasLasCuentas: false,
  ocultarConciliados: false,
  buscar: "",
};

function Conciliar() {
  const { archivo, cola } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();

  // La selección se identifica por PAGO y no por foto: una imagen puede traer
  // dos vouchers —B001-122 tiene dos depósitos de S/ 500 en la misma— y con el
  // recorte a secas solo se podía elegir uno de los dos.
  const [clave, setClave] = useState<string | null>(null);
  const [movimientoId, setMovimientoId] = useState<string | null>(null);
  const [boletaId, setBoletaId] = useState<string | null>(null);
  const [criterios, setCriterios] = useState<Criterios>(POR_DEFECTO);
  const [papel, setPapel] = useState<Papel | null>(null);

  const { data, isPending } = api.conciliarExpediente.useQuery(
    ["conciliar", archivo ?? "primero", cola],
    { query: { archivoId: archivo, cola } },
  );
  // ts-rest 3.x no tipa las respuestas de un contrato Zod 4 —el mismo apaño
  // que en las demás pantallas—, así que el tipo se recupera del esquema.
  const respuesta =
    data?.status === 200
      ? (data.body as {
          expediente: Expediente | null;
          vacio: "sin_periodo" | "sin_expedientes" | "todo_atado" | null;
        })
      : null;
  const exp = respuesta?.expediente ?? null;

  // El voucher que toca: el primero sin atar, y si están todos, el primero.
  // Se recalcula al cambiar de expediente y NO al llegar datos nuevos, para
  // que atar uno no salte a otro por su cuenta mientras se mira el resultado.
  const archivoId = exp?.archivo.id ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: el disparo es el
  // cambio de expediente; recalcularlo con cada refresco de `exp` movería el
  // voucher bajo la mano de quien acaba de corregirlo o atarlo.
  useEffect(() => {
    if (!exp) return;
    const pendiente = exp.vouchers.find((v) => !v.enlace && !v.sinAbono);
    setClave((c) => {
      const sigueEstando = c && exp.vouchers.some((v) => claveDe(v) === c);
      const primero = pendiente ?? exp.vouchers[0];
      return sigueEstando ? c : primero ? claveDe(primero) : null;
    });
  }, [archivoId]);

  const voucher = useMemo(
    () => exp?.vouchers.find((v) => claveDe(v) === clave) ?? null,
    [exp, clave],
  );

  // AL ENTRAR SIN EXPEDIENTE EN LA URL, SE FIJA EL QUE TOCÓ.
  //
  // Sin esto, atar el último voucher de un expediente lo saca de la cola y la
  // consulta —que pide «el primero de la cola»— devuelve otro distinto: la
  // pantalla saltaba sola y quien acababa de atar no llegaba a ver el
  // resultado de lo que hizo. Se avanza cuando alguien lo pide, no antes.
  useEffect(() => {
    if (!archivo && archivoId) {
      navegar({ search: { cola, archivo: archivoId }, replace: true });
    }
  }, [archivo, archivoId, cola, navegar]);

  const { data: cand, isPending: buscando } = api.candidatosDelVoucher.useQuery(
    [
      "candidatos",
      clave ?? "",
      String(criterios.diasAntes),
      String(criterios.diasDespues),
      String(criterios.tolerancia),
      String(criterios.todasLasCuentas),
      String(criterios.ocultarConciliados),
      criterios.buscar,
      voucher?.cuentaDelGrupo?.id ?? "",
    ],
    {
      query: {
        recorte: voucher?.recorte ?? "",
        // La cuenta va explícita desde aquí: la pantalla ya la tiene resuelta
        // y así el servidor no tiene que volver a deducirla de una imagen que
        // está adjunta a varios expedientes.
        cuentaId: criterios.todasLasCuentas
          ? undefined
          : (voucher?.cuentaDelGrupo?.id ?? undefined),
        diasAntes: criterios.diasAntes,
        diasDespues: criterios.diasDespues,
        tolerancia: criterios.tolerancia,
        todasLasCuentas: criterios.todasLasCuentas,
        ocultarConciliados: criterios.ocultarConciliados,
        buscar: criterios.buscar || undefined,
        limite: 25,
      },
    },
    { enabled: Boolean(voucher) },
  );
  const candidatos = cand?.status === 200 ? (cand.body as Candidatos) : null;

  // A qué boleta se aplica. Con una sola no se pregunta; con dos hay que
  // elegir, porque adivinarlo cerraría la boleta equivocada — que es el error
  // más caro de esta pantalla y el más difícil de ver después.
  const cobrables = useMemo(
    () => (exp?.boletas ?? []).filter((b) => !b.anulado),
    [exp],
  );
  useEffect(() => {
    setBoletaId((b) => {
      if (b && cobrables.some((x) => x.id === b)) return b;
      const porCobrar = cobrables.find(
        (x) => x.cobrado < (x.total ?? 0) || x.total === null,
      );
      return porCobrar?.id ?? cobrables[0]?.id ?? null;
    });
  }, [cobrables]);

  const corregir = api.corregir.useMutation();
  const atar = api.atarVoucher.useMutation();
  const desatar = api.desatarVoucher.useMutation();
  const sinAbono = api.marcarSinAbono.useMutation();

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ["conciliar"] });
    qc.invalidateQueries({ queryKey: ["candidatos"] });
    qc.invalidateQueries({ queryKey: ["inicio"] });
  };

  /** Pasa al siguiente voucher que siga sin resolver, si queda alguno. */
  const alSiguienteVoucher = (hecho: string) => {
    const resto = (exp?.vouchers ?? []).filter(
      (v) => claveDe(v) !== hecho && !v.enlace && !v.sinAbono,
    );
    if (resto[0]) setClave(claveDe(resto[0]));
    setMovimientoId(null);
    setCriterios(POR_DEFECTO);
  };

  const alAtar = () => {
    if (!voucher || !movimientoId || !boletaId) return;
    atar.mutate(
      {
        body: {
          recorte: voucher.recorte,
          pagoN: voucher.pagoN,
          movimientoId,
          comprobanteId: boletaId,
        },
      },
      {
        onSuccess: (r: Respuesta) => {
          if (r.status !== 200) {
            toast.error(mensajeDeError(r.body));
            return;
          }
          toast.success(r.body.dice ?? "Atado", {
            action: {
              label: "Deshacer",
              onClick: () =>
                desatar.mutate(
                  { body: { recorte: voucher.recorte, pagoN: voucher.pagoN } },
                  { onSuccess: refrescar },
                ),
            },
          });
          refrescar();
          alSiguienteVoucher(claveDe(voucher));
        },
        onError: () => toast.error("No se pudo atar. Inténtalo otra vez."),
      },
    );
  };

  const papelDelVoucher = (v: Voucher): Papel => ({
    clase: "imagen",
    recorte: v.recorte,
    titulo: `Voucher de ${dinero(v.importe, v.moneda)}`,
  });

  const abrirPdf = (pagina: number) => {
    if (!exp) return;
    setPapel({
      clase: "expediente",
      archivoId: exp.archivo.id,
      pagina,
      paginas: exp.archivo.paginas,
      titulo: exp.archivo.nombre,
    });
  };

  // -------------------------------------------------------------------------

  if (isPending) {
    return (
      <>
        <Cabecera migas={["Conciliación"]} titulo="Conciliar expediente" />
        <div className="space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  if (!exp) {
    return (
      <>
        <Cabecera migas={["Conciliación"]} titulo="Conciliar expediente" />
        <EmptyState
          title={TITULO_VACIO[respuesta?.vacio ?? "sin_expedientes"]}
          message={FRASE_VACIO[respuesta?.vacio ?? "sin_expedientes"]}
          action={
            respuesta?.vacio === "todo_atado" && cola === "pendientes" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navegar({ search: { cola: "todos", archivo: undefined } })
                }
              >
                Ver también los ya terminados
              </Button>
            ) : (
              <Button size="sm" asChild>
                <Link to="/carga">Cargar el mes</Link>
              </Button>
            )
          }
        />
      </>
    );
  }

  const pendientes = exp.vouchers.filter((v) => !v.enlace && !v.sinAbono);
  const elegido = candidatos?.filas.find((f) => f.id === movimientoId) ?? null;
  const boleta = cobrables.find((b) => b.id === boletaId) ?? null;

  return (
    <>
      <Cabecera
        migas={["Conciliación"]}
        titulo="Conciliar expediente"
        facetas={[
          {
            clave: "Cola",
            valor: cola === "pendientes" ? "Con vouchers sin atar" : "Todos",
            tono: cola === "pendientes" ? "acento" : "gris",
            alQuitar:
              cola === "pendientes"
                ? undefined
                : () =>
                    navegar({
                      search: { cola: "pendientes", archivo: undefined },
                    }),
          },
        ]}
        acciones={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!exp.cola.anteriorId}
              onClick={() =>
                navegar({
                  search: { cola, archivo: exp.cola.anteriorId ?? undefined },
                })
              }
            >
              <ChevronLeft className="size-4" />
              Anterior
            </Button>
            <span className="text-[12px] text-muted-foreground tabular-nums">
              {exp.cola.indice > 0
                ? `${exp.cola.indice} de ${exp.cola.total}`
                : `fuera de la cola · quedan ${exp.cola.total}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!exp.cola.siguienteId}
              onClick={() =>
                navegar({
                  search: { cola, archivo: exp.cola.siguienteId ?? undefined },
                })
              }
            >
              Siguiente
              <ChevronRight className="size-4" />
            </Button>
          </div>
        }
      />

      <TiraDelExpediente
        datos={exp}
        alAbrirPdf={abrirPdf}
        alAbrirImagen={(recorte, titulo) =>
          setPapel({ clase: "imagen", recorte, titulo })
        }
      />

      {exp.vouchers.length === 0 ? (
        <EmptyState
          title="Este expediente no trae ningún voucher"
          message="O el PDF no adjunta el comprobante del pago, o la lectura todavía no ha pasado por él. Sin voucher no hay nada que atar al banco."
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[300px_1fr]">
          <div>
            <div className="mb-2 flex items-baseline gap-2">
              <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
                Los vouchers
              </span>
              <span className="text-[11.5px] text-muted-foreground">
                {exp.vouchers.length - pendientes.length} de{" "}
                {exp.vouchers.length} resueltos
              </span>
            </div>
            <RailDeVouchers
              vouchers={exp.vouchers}
              elegido={clave}
              alElegir={(c) => {
                setClave(c);
                setMovimientoId(null);
                setCriterios(POR_DEFECTO);
              }}
            />
          </div>

          {voucher ? (
            <div className="grid gap-4 lg:grid-cols-[minmax(240px,320px)_1fr]">
              {/* --- el papel, tal cual llegó ------------------------- */}
              <figure className="rounded-lg border bg-card p-3 shadow-sm">
                <figcaption className="mb-2 flex items-center gap-2">
                  <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
                    El voucher, tal cual llegó
                  </span>
                  <button
                    type="button"
                    onClick={() => setPapel(papelDelVoucher(voucher))}
                    className="ml-auto inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
                  >
                    <Maximize2 className="size-3" />
                    Ampliar
                  </button>
                </figcaption>
                <button
                  type="button"
                  onClick={() => setPapel(papelDelVoucher(voucher))}
                  className="block w-full"
                >
                  <img
                    src={`/imagen/${voucher.recorte}`}
                    alt={`Voucher de ${dinero(voucher.importe, voucher.moneda)}`}
                    className="max-h-[420px] w-full rounded border object-contain"
                  />
                </button>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  {voucher.pagina
                    ? `Página ${voucher.pagina} del PDF`
                    : "Sin página conocida"}
                  {voucher.legible ? "" : " · el lector la marcó como ilegible"}
                </p>
              </figure>

              {/* --- lo que dice ------------------------------------- */}
              <div className="space-y-3">
                <DiceElVoucher
                  voucher={voucher}
                  guardando={corregir.isPending}
                  alCorregir={(c: Correccion) =>
                    corregir.mutate(
                      {
                        body: {
                          recorte: voucher.recorte,
                          // Los cuatro campos viajan juntos SIEMPRE, también
                          // los que no se tocaron: el servidor escribe lo que
                          // recibe, así que mandar solo el editado borraría
                          // los otros tres.
                          veredicto: "corregido",
                          importe: c.importe,
                          moneda: c.moneda,
                          fecha: c.fecha,
                          operacion: c.operacion,
                        },
                      },
                      {
                        onSuccess: (r: Respuesta) => {
                          if (r.status !== 200) {
                            toast.error(mensajeDeError(r.body));
                            return;
                          }
                          toast.success(r.body.dice ?? "Corregido");
                          refrescar();
                        },
                        onError: () => toast.error("No se pudo guardar"),
                      },
                    )
                  }
                />

                {/* Lo que ya se decidió de este voucher, con la salida para
                    rectificar. Sin el botón, corregirse obligaba a irse a la
                    auditoría a buscar qué se había atado. */}
                {voucher.enlace ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-600/40 bg-emerald-600/10 px-3 py-2.5 text-[12.5px]">
                    <Check className="size-4 shrink-0 text-emerald-700 dark:text-emerald-400" />
                    <span>
                      Atado al abono del <b>{diaMes(voucher.enlace.fecha)}</b>{" "}
                      en {voucher.enlace.banco} por{" "}
                      <b>
                        {dinero(voucher.enlace.importe, voucher.enlace.moneda)}
                      </b>
                      {voucher.enlace.quien ? ` · ${voucher.enlace.quien}` : ""}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto"
                      disabled={desatar.isPending}
                      onClick={() =>
                        desatar.mutate(
                          {
                            body: {
                              recorte: voucher.recorte,
                              pagoN: voucher.pagoN,
                            },
                          },
                          {
                            onSuccess: (r: Respuesta) => {
                              if (r.status !== 200) {
                                toast.error(mensajeDeError(r.body));
                                return;
                              }
                              toast.success(r.body.dice ?? "Soltado");
                              refrescar();
                            },
                          },
                        )
                      }
                    >
                      <Undo2 className="size-3.5" />
                      Soltar
                    </Button>
                  </div>
                ) : voucher.sinAbono ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted px-3 py-2.5 text-[12.5px] text-muted-foreground">
                    <CircleSlash className="size-4 shrink-0" />
                    <span>
                      Marcado como que no está en ningún extracto cargado
                      {voucher.sinAbono.motivo
                        ? `: ${voucher.sinAbono.motivo}`
                        : ""}
                      {voucher.sinAbono.quien
                        ? ` · ${voucher.sinAbono.quien}`
                        : ""}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto"
                      onClick={() =>
                        desatar.mutate(
                          {
                            body: {
                              recorte: voucher.recorte,
                              pagoN: voucher.pagoN,
                            },
                          },
                          {
                            onSuccess: () => {
                              toast.success("Vuelve a quedar por atar");
                              refrescar();
                            },
                          },
                        )
                      }
                    >
                      <Undo2 className="size-3.5" />
                      Volver a buscarlo
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {voucher ? (
        <TablaDeCandidatos
          datos={candidatos}
          cargando={buscando}
          voucher={voucher}
          elegido={movimientoId}
          alElegir={setMovimientoId}
          criterios={criterios}
          alCambiar={(c) => setCriterios((v) => ({ ...v, ...c }))}
        />
      ) : null}

      {/* --- la barra de acciones, anclada al pie ---------------------- */}
      {voucher ? (
        <div className="sticky bottom-0 z-10 -mx-4 flex shrink-0 flex-wrap items-center gap-2 border-t bg-background/90 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
          <Button
            size="sm"
            disabled={!movimientoId || !boletaId || atar.isPending}
            onClick={alAtar}
          >
            <Check className="size-4" />
            {elegido
              ? `Atar al abono del ${diaMes(elegido.fecha)}`
              : "Elegí un movimiento de la lista"}
          </Button>

          {/* Con dos boletas hay que decir a cuál se aplica. Con una, no se
              pregunta: un selector de una sola opción es ruido. */}
          {cobrables.length > 1 ? (
            <label className="flex items-center gap-1.5 text-[12px]">
              <span className="text-muted-foreground">Contra</span>
              <select
                value={boletaId ?? ""}
                onChange={(e) => setBoletaId(e.target.value)}
                className="h-8 rounded-md border bg-background px-2 text-[12px] outline-none"
              >
                {cobrables.map((b) => (
                  <option key={b.id} value={b.id}>
                    {etiquetaDeBoleta(b)}
                  </option>
                ))}
              </select>
            </label>
          ) : boleta ? (
            <span className="text-[12px] text-muted-foreground">
              contra {boleta.serieNumero ?? "la boleta del expediente"}
            </span>
          ) : (
            <span className="text-[12px] text-amber-700 dark:text-amber-400">
              Este expediente no tiene ninguna boleta cobrable: sin ella no se
              puede atar el pago.
            </span>
          )}

          <Button
            variant="outline"
            size="sm"
            disabled={sinAbono.isPending || Boolean(voucher.sinAbono)}
            onClick={() =>
              sinAbono.mutate(
                { body: { recorte: voucher.recorte, pagoN: voucher.pagoN } },
                {
                  onSuccess: (r: Respuesta) => {
                    if (r.status !== 200) {
                      toast.error(mensajeDeError(r.body));
                      return;
                    }
                    toast.success(r.body.dice ?? "Anotado");
                    refrescar();
                    alSiguienteVoucher(claveDe(voucher));
                  },
                },
              )
            }
          >
            <CircleSlash className="size-4" />
            No está en ningún extracto
          </Button>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-[12px] text-muted-foreground">
              {pendientes.length === 0
                ? "Este expediente está entero"
                : `Quedan ${pendientes.length} ${pendientes.length === 1 ? "voucher" : "vouchers"} de este expediente`}
            </span>
            <Button
              variant={pendientes.length === 0 ? "default" : "outline"}
              size="sm"
              disabled={!exp.cola.siguienteId}
              onClick={() =>
                navegar({
                  search: { cola, archivo: exp.cola.siguienteId ?? undefined },
                })
              }
            >
              Siguiente expediente
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      ) : null}

      <Visor papel={papel} alCerrar={() => setPapel(null)} />
    </>
  );
}

function etiquetaDeBoleta(b: Boleta): string {
  const falta = (b.total ?? 0) - b.cobrado;
  const estado =
    b.total === null
      ? ""
      : falta <= 0
        ? " · ya cobrada"
        : b.cobrado > 0
          ? ` · falta ${dinero(falta, b.moneda)}`
          : "";
  return `${b.serieNumero ?? "Sin número"} · ${dinero(b.total, b.moneda)}${estado}`;
}

/** El mensaje del servidor, que explica el porqué mejor que uno genérico. */
function mensajeDeError(body: unknown): string {
  if (body && typeof body === "object" && "message" in body) {
    const m = (body as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return "No se pudo guardar";
}
